// Service catalogue (2E-1b). A convenience only: it pre-fills a new quotation/invoice line, and every line stays fully
// editable. Saves always send the complete record (the RPC overwrites optional fields from the payload).
import { useState, useMemo } from "react";
import { DataTable, Modal, Field, Input, TextArea, Button, Notice, StatusBadge, ActionMenu, Select } from "../ui/kit/components";
import { text, money } from "../ui/kit/tokens";
import { saveService, errText } from "./api";
import { trim } from "./shared";

const blank = { name: "", description: "", unit: "", default_rate: "", sort: 0, active: true };
const record = (s) => ({ id: s.id || undefined, expected_version: s.version, name: trim(s.name), description: trim(s.description) || null,
    unit: trim(s.unit) || null, default_rate: Number(s.default_rate || 0), sort: Number(s.sort || 0), active: s.active });

function ServiceForm({ open, initial, onClose, onSaved }) {
    const [f, setF] = useState(initial || blank);
    const [busy, setBusy] = useState(false), [err, setErr] = useState("");
    const set = (k) => (e) => setF(s => ({ ...s, [k]: e.target.value }));
    const rateErr = f.default_rate !== "" && !/^\d+(\.\d{1,2})?$/.test(String(f.default_rate)) ? "Use a number with up to 2 decimals." : "";
    const save = async () => {
        setBusy(true); setErr("");
        try { await saveService({ ...record(f), reason: initial?.id ? "Service edited" : "Service added" }); onSaved(); }
        catch (e) { setErr(errText(e).includes("sales_services_name_uq") ? "A service with this name already exists." : errText(e)); }
        finally { setBusy(false); }
    };
    return (
        <Modal open={open} onClose={onClose} title={initial?.id ? "Edit service" : "New service"}
            subtitle="Fills a new line with this description, unit and rate. You can still change them on each document."
            footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={save} disabled={busy || !trim(f.name) || !!rateErr}>{busy ? "Saving…" : "Save"}</Button></>}>
            <div style={{ display: "grid", gap: 14 }}>
                <Field label="Name" required><Input value={f.name} onChange={set("name")} autoFocus /></Field>
                <Field label="Description"><TextArea value={f.description || ""} onChange={set("description")} /></Field>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    <Field label="Unit" hint="e.g. project, hour, month"><Input value={f.unit || ""} onChange={set("unit")} /></Field>
                    <Field label="Default rate (NPR)" error={rateErr}><Input inputMode="decimal" value={f.default_rate} onChange={set("default_rate")} /></Field>
                </div>
                {initial?.id && <Field label="Status"><Select value={f.active ? "1" : "0"} onChange={e => setF(s => ({ ...s, active: e.target.value === "1" }))}>
                    <option value="1">Active</option><option value="0">Inactive</option></Select></Field>}
                {err && <Notice tone="danger">{err}</Notice>}
            </div>
        </Modal>
    );
}

export default function Services({ services, reload }) {
    const [edit, setEdit] = useState(null);           // null | {} (new) | service
    const [filter, setFilter] = useState("active");
    const [err, setErr] = useState("");
    const rows = useMemo(() => services.filter(s => filter === "all" || (filter === "active") === s.active), [services, filter]);
    const toggle = async (s) => {
        setErr("");
        try { await saveService({ ...record({ ...s, active: !s.active }), reason: s.active ? "Service deactivated" : "Service reactivated" }); await reload(); }
        catch (e) { setErr(errText(e)); }
    };
    return (
        <div>
            {err && <div style={{ marginBottom: 12 }}><Notice tone="danger">{err}</Notice></div>}
            <DataTable rows={rows} searchText={s => `${s.name} ${s.description || ""} ${s.unit || ""}`} searchPlaceholder="Search services…"
                empty="No services yet. Add the services Foxbeep usually quotes for."
                filters={<Select value={filter} onChange={e => setFilter(e.target.value)} style={{ width: 140 }} aria-label="Status filter">
                    <option value="active">Active</option><option value="inactive">Inactive</option><option value="all">All</option></Select>}
                toolbarRight={<Button onClick={() => setEdit({})}>New service</Button>}
                onRowClick={s => setEdit(s)}
                columns={[
                    { key: "name", header: "Service", sortValue: s => s.name.toLowerCase(),
                        render: s => <div><div style={{ fontWeight: 600 }}>{s.name}</div>{s.description && <div style={text.caption}>{s.description}</div>}</div> },
                    { key: "unit", header: "Unit", render: s => s.unit || "—", hideOnMobile: true },
                    { key: "rate", header: "Default rate", align: "right", sortValue: s => Number(s.default_rate), render: s => money(s.default_rate) },
                    { key: "status", header: "Status", render: s => <StatusBadge status={s.active ? "on" : "off"} label={s.active ? "Active" : "Inactive"} /> },
                ]}
                rowActions={s => <ActionMenu items={[{ label: "Edit", onClick: () => setEdit(s) },
                    { label: s.active ? "Deactivate" : "Reactivate", onClick: () => toggle(s) }]} />} />
            {edit && <ServiceForm open initial={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={async () => { setEdit(null); await reload(); }} />}
        </div>
    );
}
